import PlotTypeSelector from "components/charts/modern/inputs/PlotTypeSelector";
import OnlyPremiumRender from "components/conversion/OnlyPremiumRender";
import SadFaceIcon from "components/icons/SadFaceIcon";
import OperatingSegmentsList from "components/stocks/modern/OperatingSegments/OperatingSegmentsList";
import SingleValueSelect from "components/stocks/modern/OperatingSegments/SingleValueSelect";
import calculateFilterOptions from "components/stocks/modern/OperatingSegments/calculateFilterOptions";
import type { FinancialSegmentsIndicatorName } from "components/stocks/modern/OperatingSegments/genIndicatorsByNameMap";
import genIndicatorsByNameMap from "components/stocks/modern/OperatingSegments/genIndicatorsByNameMap";
import { genStandardizedCountries } from "components/stocks/modern/OperatingSegments/genStandardizedCountries";
import { useIndicatorType, usePlotType, useSelectedYear } from "components/stocks/modern/OperatingSegments/hooks";
import type { FinancialSegmentsFields } from "components/stocks/modern/OperatingSegments/types";
import AllSegmentsHistoryColumnsChart from "components/stocks/modern/chart/AllSegmentsHistoryColumnsChart";
import SegmentsPieChart from "components/stocks/modern/chart/SegmentsPieChart";
import getYear from "date-fns/getYear";
import parseISO from "date-fns/parseISO";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { BarChartIcon, PieChartIcon } from "lucide-react";
import { invariant } from "outvariant";
import { useEffect } from "react";
import type { IntlShape } from "react-intl";
import { FormattedMessage, useIntl } from "react-intl";

interface OperatingSegmentsProps {
  kind: "GEOGRAPHIC" | "BUSINESS";
  tickerSymbols: string[];
  financialSegments: FinancialSegmentsFields;
}

export default function OperatingSegments({ kind, tickerSymbols, financialSegments }: OperatingSegmentsProps) {
  const intl = useIntl();
  const [plotType, setPlotType] = usePlotType();
  const [selectedYear, setSelectedYear] = useSelectedYear();
  const [indicatorType, setIndicatorType] = useIndicatorType();

  const indicatorsMap = genIndicatorsByNameMap(intl);
  const indicatorsOrder = Object.keys(indicatorsMap);
  const filterOptions = calculateFilterOptions(financialSegments, indicatorsOrder);
  const allSegments = [...(financialSegments?.segments || [])];

  useEffect(() => {
    if (filterOptions.allYears.length > 0) {
      setSelectedYear(filterOptions.allYears[0]);
      setIndicatorType(filterOptions.indicatorsByYear[filterOptions.allYears[0]][0]);
    }
  }, [tickerSymbols]);

  if (filterOptions.allYears.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
          <div>
            <SadFaceIcon />
          </div>

          <p>
            <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
          </p>
        </div>
      </div>
    );
  }

  invariant(filterOptions.allYears[0], "No fiscal years available");
  invariant(filterOptions.indicatorsByYear[filterOptions.allYears[0]], "No indicators available");

  if (!indicatorType || !selectedYear || !filterOptions.indicatorsByYear[selectedYear]) {
    return null;
  }

  const indicatorDetails = indicatorsMap[indicatorType as FinancialSegmentsIndicatorName];

  if (!indicatorDetails) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
          <div>
            <SadFaceIcon />
          </div>

          <p>
            <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
          </p>
        </div>
      </div>
    );
  }

  const segments = allSegments
    .map((node) => {
      const ratioIndicator = node.ratioAdjustedIndicators.find((node) => node.name === indicatorType);
      const financialIndicator = node.financialIndicators.find((node) => node.name === indicatorType);

      if (!ratioIndicator && !financialIndicator) {
        return null;
      }

      return {
        ...node,
        ratioAdjustedIndicators: node.ratioAdjustedIndicators.map((indicator) => {
          return {
            ...indicator,
            history: indicator.history.filter((item) => {
              return getYear(parseISO(item.periodEndingDate)) === selectedYear;
            }),
          };
        }),
        financialIndicators: node.financialIndicators.map((indicator) => {
          return {
            ...indicator,
            history: indicator.history.filter((item) => {
              return getYear(parseISO(item.periodEndingDate)) === selectedYear;
            }),
          };
        }),
        ratioIndicator: ratioIndicator
          ? {
              ...ratioIndicator,
              history: ratioIndicator.history.filter((item) => {
                return getYear(parseISO(item.periodEndingDate)) === selectedYear;
              }),
            }
          : null,
        financialIndicator: financialIndicator
          ? {
              ...financialIndicator,
              history: financialIndicator.history.filter((item) => {
                return getYear(parseISO(item.periodEndingDate)) === selectedYear;
              }),
            }
          : null,
      };
    })
    .filter(notUndefinedOrNull)
    .sort((a, b) => {
      return (b.financialIndicator?.value || 0) - (a.financialIndicator?.value || 0);
    });

  return (
    <OnlyPremiumRender>
      <div className="grid h-auto w-full grid-cols-1 items-start gap-4 lg:grid-cols-[max-content,1fr] lg:gap-8">
        <div className="flex h-full flex-col space-y-4 sm:w-96 sm:max-w-md">
          <div className="grid grid-cols-1 gap-4 sm:items-center sm:gap-2 lg:grid-cols-2">
            <SingleValueSelect
              disabled={filterOptions.allYears.length <= 1 || plotType === "column"}
              values={filterOptions.allYears.map((node) => ({
                label: node.toString(),
                value: node.toString(),
                description: intl.formatList(
                  filterOptions.indicatorsByYear[node]
                    .map((indicator) => {
                      if (indicator !== "Operating Revenue") {
                        return null;
                      }

                      return indicatorsMap[indicator as FinancialSegmentsIndicatorName].abbreviation;
                    })
                    .filter(notUndefinedOrNull),
                  {
                    style: "long",
                  },
                ),
              }))}
              onChange={(value) => {
                const year = parseFloat(value);
                const availableIndicators = filterOptions.indicatorsByYear[year];

                setSelectedYear(year);

                if (!availableIndicators.includes(indicatorType)) {
                  setIndicatorType(availableIndicators[0]);
                }
              }}
              value={String(selectedYear)}
            />

            <PlotTypeSelector
              value={plotType}
              onChange={setPlotType}
              className="hidden w-full rounded-xl sm:w-full sm:max-w-sm lg:flex"
              itemClassName="h-8 w-full sm:w-full"
              types={[
                {
                  type: "pie",
                  label: intl.formatMessage({
                    id: "9RIsSS",
                    defaultMessage: "Pizza",
                    description: "Tipo de visualização do gráfico",
                  }),
                  icon: <PieChartIcon />,
                },
                {
                  type: "column",
                  label: intl.formatMessage({
                    id: "hZ073v",
                    defaultMessage: "Barra",
                    description: "Tipo de visualização do gráfico",
                  }),
                  icon: <BarChartIcon />,
                },
              ]}
            />
          </div>

          <div className="h-full w-full space-y-4">
            <OperatingSegmentsList
              kind={kind}
              forYear={selectedYear}
              financialSegments={financialSegments}
              segments={segments}
            />
          </div>

          <PlotTypeSelector
            value={plotType}
            onChange={setPlotType}
            className="flex w-full rounded-xl sm:w-full sm:max-w-sm lg:hidden"
            itemClassName="h-8 w-full sm:w-full"
            types={[
              {
                type: "pie",
                label: intl.formatMessage({
                  id: "9RIsSS",
                  defaultMessage: "Pizza",
                  description: "Tipo de visualização do gráfico",
                }),
                icon: <PieChartIcon />,
              },
              {
                type: "column",
                label: intl.formatMessage({
                  id: "hZ073v",
                  defaultMessage: "Barra",
                  description: "Tipo de visualização do gráfico",
                }),
                icon: <BarChartIcon />,
              },
            ]}
          />
        </div>

        <div className="flex h-full items-center rounded-xl bg-slate-50/50">
          {segments.length > 0 && plotType === "pie" && (
            <SegmentsPieChart
              className="h-80 min-h-80 w-full md:h-max"
              segments={getAllSegmentsForPieChart(kind, indicatorType, selectedYear, allSegments, intl)}
            />
          )}

          {segments.length > 0 && filterOptions.allYears.length > 0 && plotType === "column" && (
            <AllSegmentsHistoryColumnsChart
              className="h-80 min-h-80 w-full md:h-max"
              kind={kind}
              segments={getAllSegmentsForColumnChart(kind, indicatorType, selectedYear, allSegments, intl)}
            />
          )}
        </div>
      </div>
    </OnlyPremiumRender>
  );
}

const getAllSegmentsForPieChart = (
  kind: "GEOGRAPHIC" | "BUSINESS",
  selectedIndicator: string,
  selectedYear: number,
  segments: FinancialSegmentsFields["segments"],
  intl: IntlShape,
  limit: number = kind === "GEOGRAPHIC" ? 5 : 10,
) => {
  const standardizedCountries = genStandardizedCountries(intl);
  const isGeographic = kind === "GEOGRAPHIC";

  const allSegments = segments
    .map((node) => {
      const financialIndicator = node.ratioAdjustedIndicators.find(({ name }) => {
        return name === selectedIndicator;
      });
      if (!financialIndicator) {
        return null;
      }
      const historyNode = financialIndicator.history.find((node) => {
        return getYear(parseISO(node.periodEndingDate).getTime()) === selectedYear;
      });
      if (!historyNode) {
        return null;
      }

      const standardizedCountry = standardizedCountries.find((candidate) => {
        return candidate.morningstarStandardizedName === node.name;
      });

      return {
        name: isGeographic && standardizedCountry ? standardizedCountry.name : node.name,
        value: historyNode.value,
      };
    })
    .filter(notUndefinedOrNull)
    .sort((a, b) => b.value - a.value);

  if (isGeographic) {
    const segments = allSegments.slice(0, limit);

    if (allSegments.length > limit) {
      segments.push(
        allSegments.slice(limit, allSegments.length).reduce((acc, curr) => {
          return {
            name: intl.formatMessage({
              id: "8K4Qok",
              defaultMessage: "Outros",
              description: "Complemento do gráfico de receitas",
            }),
            value: acc.value + curr.value,
          };
        }),
      );
    }

    return segments;
  }

  return allSegments.slice(0, limit);
};

const getAllSegmentsForColumnChart = (
  kind: "GEOGRAPHIC" | "BUSINESS",
  selectedIndicator: string,
  selectedYear: number,
  segments: FinancialSegmentsFields["segments"],
  intl: IntlShape,
) => {
  const standardizedCountries = genStandardizedCountries(intl);
  const isGeographic = kind === "GEOGRAPHIC";

  if (isGeographic) {
    const allSegments = segments
      .map((node) => {
        const ratioIndicator = node.ratioAdjustedIndicators.find(({ name }) => {
          return name === selectedIndicator;
        });
        if (!ratioIndicator) {
          return null;
        }
        const standardizedCountry = standardizedCountries.find((candidate) => {
          return candidate.morningstarStandardizedName === node.name;
        });
        const values = ratioIndicator.history
          .map((point) => ({
            x: getYear(parseISO(point.periodEndingDate).getTime()),
            y: point.value,
          }))
          .sort((a, b) => b.x - a.x);
        return {
          name: standardizedCountry ? standardizedCountry.name : node.name,
          yearValue: values.find((point) => point.x === selectedYear)?.y || 0,
          values,
        };
      })
      .filter(notUndefinedOrNull)
      .sort((a, b) => b.yearValue - a.yearValue);

    if (allSegments.length <= 5) {
      return allSegments.map((node) => {
        if (node.name === "Others") {
          return {
            ...node,
            name: intl.formatMessage({
              id: "8K4Qok",
              defaultMessage: "Outros",
              description: "Complemento do gráfico de receitas",
            }),
          };
        }

        return node;
      });
    }

    const otherSegment = allSegments.find((node) => node.name === "Others");
    const filteredSegments = allSegments.filter((node) => node.name !== "Others");

    const mostRelevantSegments = filteredSegments.slice(0, 5);
    const restSegments = [...filteredSegments.slice(5, allSegments.length), otherSegment].filter(notUndefinedOrNull);

    return [
      ...mostRelevantSegments,
      restSegments.reduce(
        (acc, curr) => {
          const { values } = curr;

          return {
            name: intl.formatMessage({
              id: "8K4Qok",
              defaultMessage: "Outros",
              description: "Complemento do gráfico de receitas",
            }),
            values: acc.values.map((point) => {
              const history = values.find((node) => node.x === point.x);

              return {
                x: point.x,
                y: point.y + (history ? history.y : 0),
              };
            }),
          };
        },
        {
          name: "",
          values: allSegments
            .sort((a, b) => (a.values.length > b.values.length ? -1 : 1))[0]
            .values.map((point) => ({
              x: point.x,
              y: 0,
            })),
        },
      ),
    ];
  }

  return segments
    .map((node) => {
      const financialIndicator = node.financialIndicators.find(({ name }) => {
        return name === selectedIndicator;
      });
      if (!financialIndicator) {
        return null;
      }

      const values = financialIndicator.history
        .map((point) => ({
          x: getYear(parseISO(point.periodEndingDate).getTime()),
          y: point.value,
        }))
        .sort((a, b) => b.x - a.x);
      return {
        name: node.name,
        maxValue: Math.max(...values.map((point) => point.y)),
        values,
      };
    })
    .filter(notUndefinedOrNull)
    .sort((a, b) => b.maxValue - a.maxValue);
};
